import {
  UNSURE_VALUE,
  type SeparationAllowedAnswer,
  type SeparationGroup,
} from './separation'

export interface AnswerChoice {
  key: string
  label: string
  value: string
}

type TransferKind = 'card' | 'in' | 'out'

const SORT_LABELS: Record<string, string> = {
  business: 'Business',
  personal: 'Personal',
}

// The server's option labels are the onboarding screen's, so the card's own copy
// is keyed on the answer values rather than replacing those labels.
const TRANSFER_LABELS: Record<TransferKind, Record<string, string>> = {
  card: {
    card_payment: 'Business card',
    owner_draw: 'Personal card',
  },
  in: {
    owner_contribution: 'My own money',
    business_income: 'A client paid me',
  },
  out: {
    owner_draw: 'To me personally',
    business_transfer: 'Between my accounts',
    business: 'A business expense',
  },
}

export const peerAnswersOf = (
  group: SeparationGroup
): SeparationAllowedAnswer[] =>
  group.allowedAnswers.filter((answer) => answer.value !== UNSURE_VALUE)

const transferKindOf = (group: SeparationGroup): TransferKind => {
  const values = peerAnswersOf(group).map((answer) => answer.value)
  if (values.includes('card_payment')) return 'card'
  if (
    values.includes('owner_contribution') ||
    values.includes('business_income')
  )
    return 'in'
  return 'out'
}

const labelFor = (group: SeparationGroup, value: string): string => {
  const known =
    group.kind === 'sort'
      ? SORT_LABELS[value]
      : TRANSFER_LABELS[transferKindOf(group)][value]
  return (
    known ??
    group.allowedAnswers.find((answer) => answer.value === value)?.label ??
    value
  )
}

const choiceFor = (
  group: SeparationGroup,
  value: string,
  countLabels: boolean
): AnswerChoice => {
  const label = labelFor(group, value)
  const shown =
    countLabels && group.kind === 'sort' && group.transactionCount > 1
      ? `All ${label.toLowerCase()}`
      : label
  return { key: value, label: shown, value }
}

export const choicesFor = (
  group: SeparationGroup,
  { countLabels }: { countLabels: boolean }
): AnswerChoice[] => {
  const values = peerAnswersOf(group).map((answer) => answer.value)
  const ordered = [
    ...values.filter((value) => value === group.suggestion),
    ...values.filter((value) => value !== group.suggestion),
  ]
  return ordered.map((value) => choiceFor(group, value, countLabels))
}
